// Profile/stats store — caches /profile/stats so both Settings and the
// Psychologist session gate read the same subscription state (the old code
// cached this on the app instance as _exportAllowed and reloaded ad hoc).

import { create } from 'zustand'
import { normalizeProfileStats, profileApi } from '../api/profile'
import { useAuthStore } from './authStore'
import type { ProfileStats } from '../types/api'

interface ProfileState {
  stats: ProfileStats | null
  loading: boolean
  exportAllowed: () => boolean
  hasSubscription: () => boolean
  isAccessDenied: () => boolean
  loadStats: (force?: boolean, expectedOwnerId?: string) => Promise<ProfileStats | null>
  refreshStatsForOwner: (expectedOwnerId: string) => Promise<ProfileRefreshResult>
  reset: () => void
}

export type ProfileRefreshResult =
  | { status: 'updated'; stats: ProfileStats }
  | { status: 'owner_changed' }
  | { status: 'failed' }

let profileLoadGeneration = 0

export const useProfileStore = create<ProfileState>()((set, get) => ({
  stats: null,
  loading: false,

  // export_allowed === recurring + auto_renewal → who may book a session.
  exportAllowed: () => Boolean(get().stats?.export_allowed),
  hasSubscription: () => Boolean(get().stats?.subscription_status),

  // Conservative client-side mirror of the backend's 402 paywall branches
  // (http_api/access.py::check_pwa_access -> UsageManager.check_access()).
  // Only covers the ONE case /profile/stats can actually tell us correctly:
  // a subscribed one-time token pack ("Сессия") depleted (tokens_remaining
  // <= 0) — this reads from the user's active subscription row, which is
  // wired correctly.
  //
  // Deliberately does NOT cover "no subscription, legacy token grant
  // exhausted": stats.token_limit/tokens_used come from
  // db.get_http_token_limit() (the http_token_limits table), which nothing
  // in the registration/access-check path ever populates — it's always
  // {token_limit: 0, tokens_used: 0}, for every non-subscribed user,
  // regardless of their real balance (the real balance lives in a
  // different table, token_limits, read only by the backend's own
  // check_access()). Treating token_limit<=0 as "denied" here flagged
  // brand-new users with a full balance as denied. Until /profile/stats is
  // fixed backend-side to expose the real legacy balance, this case is left
  // to the reactive 402 path, which asks the real backend check.
  //
  // A recurring subscription's weekly limit reached is similarly NOT
  // derivable from this endpoint (tokens_remaining stays null for plan_type
  // "recurring") — deliberately never flagged here either, so we don't show
  // the wrong paywall in place of the correct "wait N days" limit notice
  // (addLimitNotice).
  isAccessDenied: () => {
    const stats = get().stats
    if (!stats?.subscription_status) return false
    if (stats.plan_type === 'token_based') return (stats.tokens_remaining ?? 0) <= 0
    return false
  },

  refreshStatsForOwner: async (expectedOwnerId: string) => {
    const ownerId = expectedOwnerId
    if (!ownerId || useAuthStore.getState().userId !== ownerId) return { status: 'owner_changed' }
    const generation = ++profileLoadGeneration
    set({ loading: true })
    try {
      const stats = normalizeProfileStats(await profileApi.stats())
      if (useAuthStore.getState().userId !== ownerId) {
        return { status: 'owner_changed' }
      }
      if (generation !== profileLoadGeneration) return { status: 'failed' }
      set({ stats, loading: false })
      return { status: 'updated', stats }
    } catch {
      if (useAuthStore.getState().userId !== ownerId) {
        return { status: 'owner_changed' }
      }
      if (generation !== profileLoadGeneration) return { status: 'failed' }
      set({ loading: false })
      return { status: 'failed' }
    }
  },

  loadStats: async (force = false, expectedOwnerId?: string) => {
    const ownerId = expectedOwnerId ?? useAuthStore.getState().userId
    if (!ownerId || useAuthStore.getState().userId !== ownerId) return null
    if (get().stats && !force) return get().stats
    const result = await get().refreshStatsForOwner(ownerId)
    return result.status === 'updated' ? result.stats : null
  },

  reset: () => {
    profileLoadGeneration += 1
    set({ stats: null, loading: false })
  },
}))

useAuthStore.subscribe((state, previousState) => {
  if (state.userId !== previousState.userId) useProfileStore.getState().reset()
})
